import { useState } from 'preact/hooks';
import { rememberLang, storedLang, type Lang } from '../i18n.js';

/**
 * Public privacy policy (/privacy) and terms of service (/terms), no sign-in (owner 2026-11-02, thread "Skapa
 * integritetspolicy och användarvillkor"). Linked from the sign-in page and used on Google's consent screen.
 * Deliberately generic (owner): no supplier or tool names and no split of users into staff, freelancers or clients.
 */
export type LegalDoc = 'privacy' | 'terms';

const COMPANY = 'Färdiga Film Sweden AB';
const ADDRESS = 'Vasagatan 20, 111 20 Stockholm';
const EMAIL = 'kontakt@klarafardigafilm.se';
const UPDATED = { en: '2 November 2026', sv: '2 november 2026' };

type Section = { h?: string; p?: string[]; ul?: string[]; ol?: [string, string][] };

const mail = `<a href="mailto:${EMAIL}">${EMAIL}</a>`;

const TEXT: Record<Lang, Record<LegalDoc, { title: string; body: Section[] }> & { other: string; back: string; updated: string; nav: Record<LegalDoc, string> }> = {
  en: {
    other: 'Svenska',
    back: 'Sign in to KFF Hub',
    updated: 'Last updated',
    nav: { privacy: 'Privacy Policy', terms: 'Terms of Service' },
    privacy: {
      title: 'Privacy Policy',
      body: [
        { p: ['KFF Hub is Klara Färdiga Film’s platform for our film projects. This is a short description of how we handle personal data.'] },
        { h: 'Who is responsible', p: [`${COMPANY}, ${ADDRESS}. Contact: ${mail}.`] },
        { h: 'What we collect', ul: ['Name, email address and other contact details.', 'Information needed to plan and carry out projects.', 'Material and comments uploaded to the Hub.', 'Technical information about sign-in and use.'] },
        { h: 'Why', p: ['To give you access to the Hub, carry out our assignments and agreements, keep the service secure and meet legal requirements. We never sell personal data.'] },
        { h: 'Who has access', p: ['We use trusted providers for hosting, storage, email and administration. They process data only on our behalf. If data is processed outside the EU/EEA, an approved safeguard such as the EU standard contractual clauses applies.'] },
        { h: 'How long', p: ['As long as needed for the purpose, or as long as the law requires.'] },
        { h: 'Your rights', p: ['You can ask us to show, correct or delete your data, restrict or object to its use, and receive a copy. Contact us at the address above. You can also contact the Swedish Authority for Privacy Protection (<a href="https://www.imy.se/en/">imy.se</a>).'] },
        { h: 'Cookies', p: ['The Hub only uses essential storage for sign-in and settings. No tracking or advertising cookies.'] }
      ]
    },
    terms: {
      title: 'Terms of Service',
      body: [
        {
          ol: [
            ['The service.', `KFF Hub is provided by ${COMPANY} to those given access by KFF.`],
            ['Account.', 'Keep your sign-in details to yourself and tell us if you suspect someone else has used your account.'],
            ['Use.', 'Use the Hub only for work with KFF and do not share material or information with anyone not authorised.'],
            ['Content.', 'Uploaded material belongs to its owner. KFF may store and handle it to carry out the work.'],
            ['Availability.', 'We aim to keep the Hub working at all times but cannot guarantee it.'],
            ['Agreements come first.', 'An agreement between you or your organisation and KFF takes precedence over these terms.'],
            ['Ending.', 'KFF may close an account when the collaboration ends or if these terms are not followed.'],
            ['Law.', 'Swedish law applies.'],
            ['Contact.', mail]
          ]
        }
      ]
    }
  },
  sv: {
    other: 'English',
    back: 'Logga in i KFF Hub',
    updated: 'Senast uppdaterad',
    nav: { privacy: 'Integritetspolicy', terms: 'Användarvillkor' },
    privacy: {
      title: 'Integritetspolicy',
      body: [
        { p: ['KFF Hub är Klara Färdiga Films plattform för våra filmprojekt. Här beskriver vi kort hur vi hanterar personuppgifter.'] },
        { h: 'Vem ansvarar', p: [`${COMPANY}, ${ADDRESS}. Kontakt: ${mail}.`] },
        { h: 'Vilka uppgifter', ul: ['Namn, e-postadress och andra kontaktuppgifter.', 'Uppgifter som behövs för att planera och genomföra projekt.', 'Material och kommentarer som laddas upp i Hubben.', 'Teknisk information om inloggning och användning.'] },
        { h: 'Varför', p: ['För att ge dig tillgång till Hubben, genomföra våra uppdrag och avtal, hålla tjänsten säker och uppfylla krav i lag. Vi säljer aldrig personuppgifter.'] },
        { h: 'Vilka som får tillgång', p: ['Vi anlitar betrodda leverantörer för drift, lagring, e-post och administration. De behandlar uppgifterna bara för vår räkning. Om uppgifter behandlas utanför EU/EES finns ett godkänt skydd, till exempel EU:s standardavtalsklausuler.'] },
        { h: 'Hur länge', p: ['Så länge det behövs för syftet, eller så länge lagen kräver.'] },
        { h: 'Dina rättigheter', p: ['Du kan be oss visa, rätta eller radera dina uppgifter, begränsa eller invända mot behandlingen och få ut dem. Kontakta oss på adressen ovan. Du kan också vända dig till Integritetsskyddsmyndigheten (<a href="https://www.imy.se/">imy.se</a>).'] },
        { h: 'Cookies', p: ['Hubben använder bara nödvändig lagring för inloggning och inställningar. Inga spårnings- eller reklamcookies.'] }
      ]
    },
    terms: {
      title: 'Användarvillkor',
      body: [
        {
          ol: [
            ['Tjänsten.', `KFF Hub tillhandahålls av ${COMPANY} för den som fått åtkomst av KFF.`],
            ['Konto.', 'Håll dina inloggningsuppgifter för dig själv och säg till oss om du misstänker att någon annan använt ditt konto.'],
            ['Användning.', 'Använd Hubben bara för arbete med KFF och dela inte material eller information med obehöriga.'],
            ['Innehåll.', 'Uppladdat material tillhör den som äger det. KFF får lagra och hantera det för att genomföra arbetet.'],
            ['Tillgänglighet.', 'Vi strävar efter att Hubben alltid fungerar, men kan inte garantera det.'],
            ['Avtal går före.', 'Ett avtal mellan dig eller din organisation och KFF gäller före dessa villkor.'],
            ['Avslut.', 'KFF kan stänga ett konto när samarbetet upphör eller om villkoren inte följs.'],
            ['Lag.', 'Svensk lag gäller.'],
            ['Kontakt.', mail]
          ]
        }
      ]
    }
  }
};

/** Links to both pages, for the sign-in page's footer. */
export function LegalLinks(p: { lang: Lang }) {
  const n = TEXT[p.lang].nav;
  return (
    <p class="legal-links">
      <a href="/privacy">{n.privacy}</a>
      <span aria-hidden="true">·</span>
      <a href="/terms">{n.terms}</a>
    </p>
  );
}

export function LegalPage(p: { doc: LegalDoc }) {
  // ?lang=sv|en wins (a link straight to one language), then the visitor's saved choice
  const [lang, setLang] = useState<Lang>(() => {
    const q = new URLSearchParams(location.search).get('lang');
    return q === 'sv' || q === 'en' ? q : storedLang();
  });
  const t = TEXT[lang];
  const doc = t[p.doc];
  const other: LegalDoc = p.doc === 'privacy' ? 'terms' : 'privacy';
  document.title = `${doc.title} · KFF Hub`;
  const pick = () => {
    const next: Lang = lang === 'sv' ? 'en' : 'sv';
    rememberLang(next);
    setLang(next);
  };
  return (
    <div class="legal" lang={lang}>
      <header class="legal-top">
        <a class="legal-brand" href="/">
          <span class="brand-mark" aria-hidden="true">
            KFF
          </span>
          <span aria-hidden="true">Hub</span>
          <span class="sr-only">KFF Hub</span>
        </a>
        <button type="button" class="legal-lang" onClick={pick} lang={lang === 'sv' ? 'en' : 'sv'}>
          {t.other}
        </button>
      </header>
      <main class="legal-main">
        <h1>{doc.title}</h1>
        <p class="legal-updated">
          {t.updated}: {UPDATED[lang]}
        </p>
        {doc.body.map((s, i) => (
          <section key={i}>
            {s.h ? <h2>{s.h}</h2> : null}
            {s.p?.map((x, j) => <p key={j} dangerouslySetInnerHTML={{ __html: x }} />)}
            {s.ul ? (
              <ul>
                {s.ul.map((x, j) => (
                  <li key={j}>{x}</li>
                ))}
              </ul>
            ) : null}
            {s.ol ? (
              <ol>
                {s.ol.map(([b, x], j) => (
                  <li key={j}>
                    <b>{b}</b> <span dangerouslySetInnerHTML={{ __html: x }} />
                  </li>
                ))}
              </ol>
            ) : null}
          </section>
        ))}
        <footer class="legal-foot">
          <a href={`/${other}`}>{t.nav[other]}</a>
          <span aria-hidden="true">·</span>
          <a href="/">{t.back}</a>
        </footer>
      </main>
    </div>
  );
}
